/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { useLayoutEffect, useRef, useState } from 'react';

import { COLORS, FONT } from '@/Client/Partials/CloseoutReviewFlow/closeoutTokens';
import { FocusedOrgProductFlyout } from '@/Client/Partials/FocusedScopeEditor/FocusedOrgProductFlyout';
import { useOrgProduct } from '@/Client/State';

import { pxToRem } from '@/Client/Styles/pxToRem';
import { TILE_RADIUS_PX } from './progressAltMetrics';
import type { ProgressAltLineItem, ProgressAltScopeOption } from './progressAltScopeOutlineLib';
import { FOCUS_RING_STYLE } from './progressAltStyles';

/** How much of an option's prose stands unexpanded. */
const DESCRIPTION_LINE_CLAMP = 2;

const SECTION_STYLE = css`
  label: PROGRESS_ALT_SCOPE_OUTLINE_STYLE;
  display: flex;
  flex-direction: column;
  padding: 18px 0 24px;
  border-top: 1px solid ${COLORS.strokeFaded};
`;

const SECTION_TITLE_STYLE = css`
  padding: 0 22px 4px;
  font-family: ${FONT.mono};
  font-size: ${pxToRem(10)};
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${COLORS.midtoneFaded};
`;

/**
 * Options read as sub-headings rather than as cards: the reader is going down a list of what the task
 * calls for, and a box around each one would break that list into unrelated blocks.
 */
const OPTION_STYLE = css`
  display: flex;
  flex-direction: column;
  padding: 14px 22px 0;
`;

const OPTION_TITLE_STYLE = css`
  font-family: ${FONT.display};
  font-size: ${pxToRem(14)};
  font-weight: 700;
  line-height: 1.286;
  color: ${COLORS.ink};
`;

/**
 * Two lines by default, all of them once asked. Clamped rather than truncated at a character count, so
 * the cut lands on whatever the pane's actual width made the second line.
 */
const DESCRIPTION_STYLE = css`
  display: block;
  width: 100%;
  margin-top: 4px;
  padding: 0;
  border: 0;
  background: transparent;
  color: #4f4942;
  font-size: ${pxToRem(13)};
  line-height: 1.385;
  text-align: left;

  ${FOCUS_RING_STYLE}
`;

const DESCRIPTION_CLICKABLE_STYLE = css`
  cursor: pointer;

  &:hover {
    color: ${COLORS.ink};
  }
`;

const DESCRIPTION_CLAMPED_STYLE = css`
  display: -webkit-box;
  -webkit-line-clamp: ${DESCRIPTION_LINE_CLAMP};
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

/** Marks that there is more behind the clamp. Shown only when there actually is. */
const DESCRIPTION_MORE_STYLE = css`
  display: inline-block;
  margin-top: 2px;
  font-family: ${FONT.mono};
  font-size: ${pxToRem(10)};
  font-weight: 700;
  letter-spacing: 0.06em;
  color: ${COLORS.midtoneFaded};
`;

const LINE_ITEMS_STYLE = css`
  display: flex;
  flex-direction: column;
  margin-top: 10px;
`;

const LINE_ITEM_STYLE = css`
  width: 100%;
  display: grid;
  grid-template-columns: 44px minmax(0, 1fr) auto;
  align-items: center;
  gap: 10px;
  padding: 6px 0;
  border: 0;
  border-top: 1px solid ${COLORS.strokeFaded};
  background: transparent;
  text-align: left;
`;

/** Only a row with a catalogue product behind it opens anything, so only that row invites a click. */
const LINE_ITEM_CLICKABLE_STYLE = css`
  cursor: pointer;

  &:hover {
    background: ${COLORS.wash};
  }

  ${FOCUS_RING_STYLE}
`;

/** Sized to the smallest thing that still reads as the product rather than as a swatch. */
const PRODUCT_PHOTO_STYLE = css`
  width: 44px;
  height: 44px;
  overflow: hidden;
  border: 1px solid ${COLORS.strokeFaded};
  border-radius: ${TILE_RADIUS_PX}px;
  background: ${COLORS.surfaceMuted};
`;

const PRODUCT_IMAGE_STYLE = css`
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const LINE_ITEM_TEXT_STYLE = css`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const LINE_ITEM_TITLE_STYLE = css`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: ${pxToRem(13)};
  font-weight: 500;
  color: ${COLORS.ink};
`;

const LINE_ITEM_QUANTITY_STYLE = css`
  font-family: ${FONT.mono};
  font-size: ${pxToRem(9)};
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${COLORS.midtoneFaded};
`;

/** Right-ranged and tabular, so a column of subtotals can be read down rather than one at a time. */
const LINE_ITEM_SUBTOTAL_STYLE = css`
  font-size: ${pxToRem(13)};
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  text-align: right;
  white-space: nowrap;
  color: ${COLORS.ink};
`;

const EMPTY_OPTION_STYLE = css`
  margin-top: 8px;
  font-size: ${pxToRem(13)};
  color: ${COLORS.midtoneFaded};
`;

/**
 * Whether the clamp is actually hiding anything, measured rather than guessed at a character count —
 * the pane's width decides where the second line ends, and a threshold would be wrong at every other
 * width. Re-measured when the text changes, and reported false where nothing lays out (jsdom), which
 * keeps the affordance from appearing over prose that is already whole.
 */
const useIsClamped = (text: string | undefined): [boolean, (element: HTMLElement | null) => void] => {
  const [clamped, setClamped] = useState(false);
  const elementRef = useRef<HTMLElement | null>(null);

  useLayoutEffect(() => {
    const element = elementRef.current;
    setClamped(!!element && element.scrollHeight > element.clientHeight + 1);
  }, [text]);

  return [clamped, element => {
    elementRef.current = element;
  }];
};

const ProgressAltOptionDescription = ({ description }: { description: string }) => {
  const [expanded, setExpanded] = useState(false);
  const [clamped, setElement] = useIsClamped(description);
  const canExpand = clamped || expanded;

  return (
    <>
      <span
        ref={setElement}
        css={[DESCRIPTION_STYLE, canExpand && DESCRIPTION_CLICKABLE_STYLE, !expanded && DESCRIPTION_CLAMPED_STYLE]}
        role={canExpand ? 'button' : undefined}
        tabIndex={canExpand ? 0 : undefined}
        aria-expanded={canExpand ? expanded : undefined}
        onClick={canExpand ? () => setExpanded(!expanded) : undefined}
        onKeyDown={canExpand
          ? event => {
            if (event.key !== 'Enter' && event.key !== ' ') return;
            event.preventDefault();
            setExpanded(!expanded);
          }
          : undefined}
      >
        {description}
      </span>
      {clamped && !expanded ? <span css={DESCRIPTION_MORE_STYLE} aria-hidden='true'>[…]</span> : null}
    </>
  );
};

/**
 * One product on the scope. Where a catalogue product stands behind it, the row opens the same product
 * flyout the scope editor uses — the reader gets the full product without this pane having to carry it,
 * and there is one place where product details are written.
 *
 * The lookup lives here rather than in the transform because it is a hook, and a row is the smallest
 * thing that can hold one per line item.
 */
const ProgressAltLineItemRow = ({ lineItem }: { lineItem: ProgressAltLineItem }) => {
  const { orgProduct } = useOrgProduct(lineItem.productSkuId);
  const [flyoutOpen, setFlyoutOpen] = useState(false);
  const imageUrl = lineItem.imageUrl ?? orgProduct?.image;

  return (
    <>
      <button
        type='button'
        css={[LINE_ITEM_STYLE, orgProduct && LINE_ITEM_CLICKABLE_STYLE]}
        data-testid='progress-alt-line-item'
        aria-label={orgProduct ? `Show product details for ${lineItem.title}` : undefined}
        disabled={!orgProduct}
        onClick={() => setFlyoutOpen(true)}
      >
        <span css={PRODUCT_PHOTO_STYLE}>
          {imageUrl ? <img css={PRODUCT_IMAGE_STYLE} src={imageUrl} alt='' loading='lazy' decoding='async' /> : null}
        </span>
        <span css={LINE_ITEM_TEXT_STYLE}>
          <span css={LINE_ITEM_TITLE_STYLE} title={lineItem.title}>{lineItem.title}</span>
          <span css={LINE_ITEM_QUANTITY_STYLE}>{lineItem.quantityLabel}</span>
        </span>
        <span css={LINE_ITEM_SUBTOTAL_STYLE}>{lineItem.subtotalLabel ?? '—'}</span>
      </button>
      <FocusedOrgProductFlyout
        orgProduct={orgProduct ?? null}
        open={flyoutOpen}
        onClose={() => setFlyoutOpen(false)}
      />
    </>
  );
};

/**
 * What the task is made of: every scoped option as a sub-heading, every scoped line item under it as a
 * product, a quantity and a subtotal.
 *
 * Deliberately not the whole line item. Unit price, SKU, brand, vendor and status all exist on the
 * data and all stay off this list — the reader is here to look at photos, and this is context for them
 * rather than the scope report, which is a click away in the editor.
 */
export const ProgressAltScopeOutline = ({ options }: { options: readonly ProgressAltScopeOption[] }) => {
  if (!options.length) return null;

  return (
    <section css={SECTION_STYLE} data-testid='progress-alt-scope-outline'>
      <h3 css={SECTION_TITLE_STYLE}>Scope</h3>
      {options.map(option => (
        <div key={option.id} css={OPTION_STYLE} data-testid='progress-alt-scope-option'>
          <span css={OPTION_TITLE_STYLE}>{option.title}</span>
          {option.description ? <ProgressAltOptionDescription description={option.description} /> : null}
          {option.lineItems.length
            ? (
              <div css={LINE_ITEMS_STYLE}>
                {option.lineItems.map(lineItem => <ProgressAltLineItemRow key={lineItem.id} lineItem={lineItem} />)}
              </div>
            )
            : <span css={EMPTY_OPTION_STYLE}>No line items</span>}
        </div>
      ))}
    </section>
  );
};
